import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import { ConditionalWrapper } from 'components/conditional-wrapper';
import FullPageScreen from 'components/full-page-screen';
import LoadingAnimation from 'components/loading-animation';
import PaymentMethod from 'components/payment-method';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useOrderContext } from 'state/order';
import { useCartEntriesDayParts } from 'state/order/hooks/use-cart-entries-day-parts';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import { usePaymentContext } from 'state/payment';
import { CASH_ACCOUNT_IDENTIFIER } from 'state/payment/constants';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import { PlaceOrderButton } from '../../../place-order-button';
import SectionHeading from '../../../section-heading';
import { AddNewCreditCardWrapper } from '../../add-new-credit-card-wrapper';
import DeliveryCancellationPolicy from '../../delivery-cancellation-policy';
import PaymentSelection from '../../payment-selection';
import { PaymentInfoBackground, PaymentSection } from '../../styled';
import { OrderPaymentProps } from '../../types';
import Totals from '../Totals';
import { useOrderPayment } from '../use-order-payment';

import { PaymarkHostedPaymentForm } from './paymark-form/paymark-hosted-payment-form';
import { IPaymarkPaymentVaultLinkResult, OutcomeResult } from './types';

const StyledFullPageScreen = styled(FullPageScreen)`
  background: ${Styles.color.background};

  h1 {
    text-transform: capitalize;
  }
`;

export const PaymarkHostedPagePayment: React.FC<OrderPaymentProps> = ({
  serverOrder,
  cartEntries,
  onPriceOrder,
  pricedAndAvailable,
}) => {
  const { formatMessage } = useIntl();
  const [vaultedPaymentData, setVaultedPaymentData] =
    useState<IPaymarkPaymentVaultLinkResult | null>(null);

  const { cartEntriesActiveDayParts } = useCartEntriesDayParts(cartEntries);
  const order = useOrderContext();
  const { serviceMode, isDelivery, isCurbside, isTableService } = useServiceModeContext();
  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);
  const [isPaymentError, setIsPaymentError] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const { checkoutPaymentMethodId } = usePaymentContext();
  const mParticle = useMParticleContext();

  const { enableOrderTimedFire } = useOrderTimedFire({
    serverOrder,
    cartEntriesActiveDayParts,
  });

  const { getErrorMessages } = useGetPaymentErrors();
  const SHOW_EXPANDABLE_CONTENT_ADD_NEW_CREDIT_CAR = false;

  const {
    enableDelivery,
    ErrorDialog,
    handlePaymentMethodSelected,
    handlePayMarkSuccess,
    isCashPayment,
    isPlacingOrder,
    isValidTip,
    offerIsValid,
    paymentMethods,
    placeOrderAriaLabel,
    setCommitOrderAfterPricing,
    setIsHandlingOrderCommit,
    setShowAddPaymentMethod,
    shouldConfirmStoreOnCheckout,
    showAddPaymentMethod,
    showPaymentMethodSelection,
  } = useOrderPayment({
    enableOrderTimedFire,
    onPriceOrder,
    serverOrder,
  });

  const paymentErrorMessages = getErrorMessages({
    invalidOfferError: !offerIsValid,
    invalidPaymentMethodError: isPaymentError,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const onPaymentOutcome = useCallback(
    (outcome: OutcomeResult) => {
      setIsProcessingPayment(false);
      if (vaultedPaymentData) {
        setVaultedPaymentData(null);
      }

      if (outcome.type === 'Failed') {
        logger.error({
          message: 'PayMark Payment: Hosted page integration failed',
          outcome,
        });

        setIsPaymentError(true);
        setIsCheckoutErrorsModalOpen(true);
        if (showAddPaymentMethod) {
          // if the "Add New Payment Method" section is expanded,
          // let's close it and re-open it a bit later to
          // reload the hosted payment form
          setShowAddPaymentMethod(false);
          setTimeout(() => setShowAddPaymentMethod(true), 1000);
        }
        return;
      }
      const { merchantAccount, paymentMethod, pspReference } = outcome;

      const payloadPaymark = {
        merchantAccount,
        pspReference,
        isCardNumberValid: true,
        cardNumber: paymentMethod.storePaymentMethod ? paymentMethod.storedPaymentMethodId : '',
        nameOnCard: paymentMethod.cardholderName,
        isExpiryValid: true,
        card: {
          bin: paymentMethod.bin,
          last4: paymentMethod.last4,
          expiryMonth: paymentMethod.expiryMonth,
          expiryYear: paymentMethod.expiryYear,
        },
        cardType: paymentMethod.brand,
        cvv: '',
        saveCard: paymentMethod.storePaymentMethod,
        expiry: `${paymentMethod.expiryMonth}${paymentMethod.expiryYear}`,
        billingStreetAddress: null,
        billingCity: null,
        billingApt: null,
        billingZip: '',
        billingCountry: null,
        billingState: null,
        billingAddressSameAsDelivery: false,
      };

      handlePayMarkSuccess(payloadPaymark);

      mParticle.logEvent(CustomEventNames.BUTTON_CLICK, EventTypes.Navigation, {
        Name: CustomEventNames.BUTTON_CLICK_PLACE_ORDER,
      });
      setIsHandlingOrderCommit(true);
    },
    [
      vaultedPaymentData,
      handlePayMarkSuccess,
      mParticle,
      setIsHandlingOrderCommit,
      showAddPaymentMethod,
      setShowAddPaymentMethod,
    ]
  );

  if (!serverOrder) {
    return null;
  }

  // Handles generating and auto-submitting a vaulted payment form if the user selects to pay with a saved credit card
  const handleVaultedPaymarkPayment = async () => {
    if (!selectedMethod || !selectedMethod.accountIdentifier) {
      return;
    }

    // If any error is present at last step we abort payment and show error modal
    if (paymentErrorMessages?.length) {
      setIsCheckoutErrorsModalOpen(true);
      return;
    }

    setIsProcessingPayment(true);

    if (selectedMethod.accountIdentifier === CASH_ACCOUNT_IDENTIFIER) {
      shouldConfirmStoreOnCheckout(true);
      if (isCurbside && onPriceOrder) {
        setCommitOrderAfterPricing(true);
        return onPriceOrder({
          customerName: `CS-${LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION)}`,
        });
      }
      if (isTableService && onPriceOrder) {
        setCommitOrderAfterPricing(true);
        return onPriceOrder({
          customerName: `TABLE-${LocalStorage.getItem(StorageKeys.TABLE_NUMBER)}`,
        });
      }
      setIsHandlingOrderCommit(true);
      setIsProcessingPayment(false);
    }

    setIsHandlingOrderCommit(true);
  };

  const showPlaceOrderButton =
    (selectedMethod?.credit && !vaultedPaymentData) ||
    selectedMethod?.paypal ||
    selectedMethod?.cash;

  return (
    <StyledFullPageScreen heading={formatMessage({ id: 'orderPayment' })} closeHref={routes.cart}>
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          checkoutErrors={paymentErrorMessages}
          closeModal={() => {
            setIsCheckoutErrorsModalOpen(false);
          }}
        />
      )}
      <PaymentSection data-testid="cart-hosted-page-payment">
        <SectionHeading id="payment-method-header">
          {formatMessage({ id: 'paymentDetails' })}
        </SectionHeading>
        <PaymentSelection>
          <Totals
            serverOrder={serverOrder}
            order={order}
            serviceMode={serviceMode}
            isDisabled={isProcessingPayment || isPlacingOrder}
          />
          {isDelivery && enableDelivery && <DeliveryCancellationPolicy />}
          <PaymentInfoBackground $hostedPayment>
            {showPaymentMethodSelection && (
              <PaymentMethod
                aria-expanded="true"
                aria-labelledby="payment-method-header"
                hasBorder={false}
                isAddCardOpen={showAddPaymentMethod}
                methods={paymentMethods}
                openAddCardOnClick={setShowAddPaymentMethod}
                handleSelect={handlePaymentMethodSelected}
                selectedMethodId={checkoutPaymentMethodId}
              />
            )}
            {showAddPaymentMethod && (
              <ConditionalWrapper
                condition={SHOW_EXPANDABLE_CONTENT_ADD_NEW_CREDIT_CAR}
                wrapper={children => <AddNewCreditCardWrapper>{children}</AddNewCreditCardWrapper>}
              >
                <PaymarkHostedPaymentForm
                  rbiOrderId={serverOrder.rbiOrderId ?? ''}
                  onResult={onPaymentOutcome}
                />
              </ConditionalWrapper>
            )}
            {isPlacingOrder && !isCashPayment && (
              <LoadingAnimation fillColor={Styles.color.primary} />
            )}
          </PaymentInfoBackground>
          {showPlaceOrderButton && (
            <PlaceOrderButton
              confirmation={false}
              disabled={isProcessingPayment || isPlacingOrder}
              isLoading={isProcessingPayment || isPlacingOrder}
              margin={selectedMethod ? '1rem 0 0' : '0.5rem 0 0'}
              onClick={handleVaultedPaymarkPayment}
              placeOrderAriaLabel={placeOrderAriaLabel}
            />
          )}
        </PaymentSelection>
        <ErrorDialog />
      </PaymentSection>
    </StyledFullPageScreen>
  );
};

export default PaymarkHostedPagePayment;
